import { MILL_1 } from "@/setup/mill-config/mill-config";
import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import type { RollChangeStats } from "@/pages/mill/schedule/calendar/models/CampaignEventData";
import type { Section } from "../models/Section";

export interface UpcomingEvent {
	SectionId: number;
	SectionName: string;
	EventIndex: number;
}

export interface SectionsResponse {
	sections: Section[];
	upcomingEvents: UpcomingEvent[];
	recentEvents: UpcomingEvent[];
	rollChangeStats: RollChangeStats | null;
}

export const getSections = async (
	params: {
		sectionId?: number | null;
		start?: Date | null;
		defaultAreaId?: string | number | null;
		shiftId?: number | null;
	},
	signal?: AbortSignal,
): Promise<SectionsResponse> => {
	const apiService = new MESApiService();
	const shiftId = params.shiftId;
	const isShiftScoped = shiftId !== null && shiftId !== undefined;

	const parameters: spParameter[] = [];

	if (params.sectionId)
		parameters.push({ name: "SectionId", value: params.sectionId });

	if (params.sectionId && params.start)
		parameters.push({ name: "Start", value: params.start.toISOString() });

	if (isShiftScoped) parameters.push({ name: "ShiftId", value: shiftId });

	const resp = await apiService.callV2(
		`${Number(params.defaultAreaId) === MILL_1.id ? "[M1].[GetSections]" : "[M2].[GetSections]"}`,
		parameters,
		signal,
	);

	if (resp.ok) {
		const rows = get(resp, "data.tables[0].rows", []) as Section[];
		const orderedRows = isShiftScoped
			? rows
			: rows.toSorted((a, b) => (a.SortOrder ?? 0) - (b.SortOrder ?? 0));

		const processedRows = orderedRows.map((section, index) => ({
			...section,
			MillYield: section.MillYield ?? 0,
			FinishedYield: section.FinishedYield ?? 0,
			IsFirstSection: index === 0,
			IsLastSection: index === orderedRows.length - 1,
		}));

		const statsRows = get(resp, "data.tables[3].rows", []);
		return {
			sections: processedRows as Section[],
			upcomingEvents: get(resp, "data.tables[1].rows", []) as UpcomingEvent[],
			recentEvents: get(resp, "data.tables[2].rows", []) as UpcomingEvent[],
			rollChangeStats: statsRows.length > 0 ? statsRows[0] : null,
		};
	} else {
		throw new Error(resp.message || "Error fetching sections");
	}
};

export const getSectionsByPart = async (
	params: { partId: number | null },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [{ name: "PartId", value: params.partId }];

	const resp = await apiService.callV2(
		"[M2].[GetSectionsByPart]",
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as Section[];
	} else {
		throw new Error(resp.message || "Error fetching sections");
	}
};

export const setSectionsToPart = async (partId: number, section: Section[]) => {
	const apiService = new MESApiService();

	const standsIds = section.map((item) => item.SectionId);
	const standIdsString = standsIds.join(",");

	const parameters: spParameter[] = [
		{ name: "PartId", value: partId },
		{ name: "SectionIds", value: standIdsString },
	];

	return await apiService.callV2("[M2].[SetSectionsToPart]", parameters);
};
